class ContactFormSelect extends HTMLElement {
  #controller = null;
  #trigger = null;
  #list = null;
  #valueInput = null;
  #label = null;
  #options = [];
  #activeIndex = -1;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#trigger = this.querySelector('[data-contact-select-trigger]');
    this.#list = this.querySelector('[data-contact-select-list]');
    this.#valueInput = this.querySelector('[data-contact-select-value]');
    this.#label = this.querySelector('[data-contact-select-label]');
    this.#options = [...this.querySelectorAll('[role="option"]')];

    this.#trigger?.addEventListener('click', this.#onTriggerClick.bind(this), { signal });
    this.#trigger?.addEventListener('keydown', this.#onTriggerKeydown.bind(this), { signal });
    this.#list?.addEventListener('click', this.#onOptionClick.bind(this), { signal });
    this.#list?.addEventListener('keydown', this.#onListKeydown.bind(this), { signal });
    document.addEventListener('click', this.#onDocumentClick.bind(this), { signal });
    this.closest('form')?.addEventListener('submit', this.#onFormSubmit.bind(this), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get #isOpen() {
    return this.#trigger?.getAttribute('aria-expanded') === 'true';
  }

  #onTriggerClick(event) {
    event.preventDefault();
    if (this.#isOpen) {
      this.#close();
      return;
    }

    this.#open();
  }

  #onTriggerKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.#open();
      this.#setActiveIndex(event.key === 'ArrowUp' ? this.#options.length - 1 : 0);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#open();
    }
  }

  #onListKeydown(event) {
    if (!this.#isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#close(true);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#setActiveIndex(Math.min(this.#activeIndex + 1, this.#options.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#setActiveIndex(Math.max(this.#activeIndex - 1, 0));
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      this.#setActiveIndex(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      this.#setActiveIndex(this.#options.length - 1);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (this.#activeIndex >= 0) {
        this.#selectOption(this.#options[this.#activeIndex]);
      }
    }
  }

  #onOptionClick(event) {
    const option = event.target.closest('[role="option"]');
    if (!option || !this.#list?.contains(option)) return;

    this.#selectOption(option);
  }

  #onDocumentClick(event) {
    if (!this.#isOpen) return;
    if (this.contains(event.target)) return;

    this.#close();
  }

  #onFormSubmit(event) {
    if (this.#valueInput?.value) return;

    event.preventDefault();
    this.#trigger?.setAttribute('aria-invalid', 'true');
    this.#open();
    this.#trigger?.focus();
  }

  #open() {
    if (!this.#trigger || !this.#list) return;

    this.#trigger.setAttribute('aria-expanded', 'true');
    this.#list.hidden = false;
    this.classList.add('is-open');

    const selectedIndex = this.#options.findIndex((option) => option.hasAttribute('data-selected'));
    this.#setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }

  #close(focusTrigger = false) {
    if (!this.#trigger || !this.#list) return;

    this.#trigger.setAttribute('aria-expanded', 'false');
    this.#list.hidden = true;
    this.classList.remove('is-open');
    this.#clearActive();

    if (focusTrigger) {
      this.#trigger.focus();
    }
  }

  #selectOption(option) {
    if (!option || !this.#valueInput || !this.#label || !this.#trigger) return;

    const value = option.dataset.value || '';

    for (const item of this.#options) {
      item.removeAttribute('data-selected');
      item.setAttribute('aria-selected', 'false');
    }

    option.setAttribute('data-selected', '');
    option.setAttribute('aria-selected', 'true');

    this.#valueInput.value = value;
    this.#label.textContent = value;
    this.#trigger.classList.add('is-selected');
    this.#trigger.removeAttribute('aria-invalid');
    this.#close(true);
  }

  #setActiveIndex(index) {
    if (!this.#options.length) return;

    this.#clearActive();
    this.#activeIndex = index;
    const option = this.#options[index];
    if (!option) return;

    option.classList.add('is-active');
    option.focus();
  }

  #clearActive() {
    for (const option of this.#options) {
      option.classList.remove('is-active');
    }

    this.#activeIndex = -1;
  }
}

if (!customElements.get('contact-form-select')) {
  customElements.define('contact-form-select', ContactFormSelect);
}
